import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ThemeSelector } from "./common/ThemeSelector";

const Desktop = ({ className = "" }) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogoClick = () => {
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleSignUpClick = () => {
    navigate('/signup');
    setMobileMenuOpen(false);
  };

  const handleSignInClick = () => {
    navigate('/signin');
    setMobileMenuOpen(false);
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  const handleLogoutClick = async () => {
    await signOut();
    navigate('/');
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  const menuItems = user ? [
    { text: 'Home', onClick: handleLogoClick },
    { text: 'Dashboard', onClick: handleDashboardClick },
    { text: 'Logout', onClick: handleLogoutClick },
  ] : [
    { text: 'Home', onClick: handleLogoClick },
    { text: 'Sign Up', onClick: handleSignUpClick },
    { text: 'Sign In', onClick: handleSignInClick },
  ];

  return (
    <header className={`navbar bg-base-100 px-4 md:px-20 py-4 ${className}`}>
      {/* Logo */}
      <div className="flex-1">
        <div className="flex items-center cursor-pointer" onClick={handleLogoClick}>
          <img
            className="w-9 h-9 md:w-11 md:h-11"
            loading="lazy"
            alt="Logo"
            src="/group-39795.svg"
          />
          <span className="ml-2 md:ml-3 text-xl md:text-2xl font-bold text-primary">
            StockAlgoTrader
          </span>
        </div>
      </div>

      {/* Desktop Navigation */}
      {!isMobile && (
        <div className="flex-none">
          <nav className="flex items-center gap-2">
            {user ? (
              <>
                <ThemeSelector />
                <button className="btn btn-ghost" onClick={handleDashboardClick}>
                  Dashboard
                </button>
                <div className="dropdown dropdown-end">
                  <div
                    tabIndex={0}
                    role="button"
                    className="btn btn-ghost gap-2"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <span className="font-medium">{user.name || 'User'}</span>
                    <div className="avatar placeholder">
                      <div className="bg-primary text-primary-content rounded-full w-8">
                        <span className="text-sm">{user.name ? user.name[0].toUpperCase() : 'U'}</span>
                      </div>
                    </div>
                  </div>
                  <ul tabIndex={0} className="dropdown-content menu bg-base-200 rounded-box z-[1] w-52 p-2 shadow mt-2">
                    <li><button onClick={handleDashboardClick}>Dashboard</button></li>
                    <li><button onClick={handleLogoutClick}>Logout</button></li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <ThemeSelector />
                <button className="btn btn-ghost" onClick={handleSignInClick}>
                  Sign In
                </button>
                <button className="btn btn-primary" onClick={handleSignUpClick}>
                  Sign Up
                </button>
              </>
            )}
          </nav>
        </div>
      )}

      {/* Mobile Menu Button */}
      {isMobile && (
        <div className="flex-none">
          <button
            className="btn btn-ghost btn-square"
            onClick={() => setMobileMenuOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-6 h-6 stroke-current">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>
      )}

      {/* Mobile Menu Drawer */}
      <div className={`drawer drawer-end ${mobileMenuOpen ? 'drawer-open' : ''}`} style={{ position: 'fixed', top: 0, right: 0, zIndex: 9999, pointerEvents: mobileMenuOpen ? 'auto' : 'none' }}>
        <input
          type="checkbox"
          className="drawer-toggle"
          checked={mobileMenuOpen}
          onChange={() => setMobileMenuOpen(!mobileMenuOpen)}
        />
        <div className="drawer-side">
          <label className="drawer-overlay" onClick={() => setMobileMenuOpen(false)}></label>
          <div className="menu bg-base-100 min-h-full w-64 p-4">
            <div className="flex justify-end mb-4">
              <button className="btn btn-ghost btn-square" onClick={() => setMobileMenuOpen(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="inline-block w-6 h-6 stroke-current">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            {user && (
              <div className="flex items-center gap-3 p-4 border-b border-base-300 mb-4">
                <div className="avatar placeholder">
                  <div className="bg-primary text-primary-content rounded-full w-10">
                    <span>{user.name ? user.name[0].toUpperCase() : 'U'}</span>
                  </div>
                </div>
                <span className="font-bold">{user.name || 'User'}</span>
              </div>
            )}
            <div className="p-4 pt-0">
              <ThemeSelector />
            </div>
            <ul className="menu">
              {menuItems.map((item) => (
                <li key={item.text}>
                  <button onClick={item.onClick} className="text-base-content">
                    {item.text}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Desktop;
